import { TermCourseServant } from '../../term_courses/servants/term_course_servant';
import { SectionRegistrationServant } from '../../course_scheduler/servants/section_registration_servant';
import { SectionStatusServant } from '../../course_scheduler/servants/section_status_servant';
import { Section } from '../builders/section_builder';
import { t_key } from 'core-ts-types';
import { Metasource } from 'utility-ts-datasource';
import { Datasource, IDatasource } from 'utility-ng-kernel';
import {
  ICourse,
  ISectionType,
  IWaitlistStatus,
  ICampus,
  ISectionLabel
} from '../../../interfaces/scheduler_interfaces';
import { ITermCoursesDatasource } from '../../term_courses/datasources/term_courses_datasource';
import { ITermSectionTypesDatasource } from '../../term_courses/datasources/term_section_types_datasource';
import { IWaitlistStatusDatasource } from '../../course_scheduler/datasources/waitlist_status_datasource';
import { LinkedSectionsModel } from '../models/linked_sections_model';
import { ISectionTimesDatasource } from './section_times_datasource';
import * as _4 from 'lodash4';
import { SectionTime } from '../builders/section_time_builder';
import { ICampusesDatasource } from './campuses_datasource';
import { ISectionAvailabilityDatasource } from './section_availability_datasource';
import { ISectionLabelsDatasource } from './section_labels_datasource';
import { TermScheduleFlags } from '../../course_scheduler/services/term_schedule_flags';

export interface ISectionsDatasource extends IDatasource<Section> {}

export interface ISectionsMeta {
  _course: ICourse;
  _section_type: ISectionType;
  _availability: IWaitlistStatus;
  _linked_sections: number[];
  _section_times: SectionTime[];
  _campus: ICampus;
  _fullLocationName: string;
  _section_status: IWaitlistStatus;
  _isEligibleForRegistration?: boolean;
  _section_labels: ISectionLabel[];
  is_waitlisted?: boolean;
  id: t_key;
}
angular.module('capability-ng-scheduler').factory(
  'ngSectionsDatasource',
  (
    ngTermCoursesDatasource: ITermCoursesDatasource,
    ngTermSectionTypesDatasource: ITermSectionTypesDatasource,
    ngSectionAvailabilityDatasource: ISectionAvailabilityDatasource,
    ngLinkedSectionsModel: LinkedSectionsModel,
    ngSectionTimesDatasource: ISectionTimesDatasource,
    ngWaitlistStatusDatasource: IWaitlistStatusDatasource,
    ngCampusesDatasource: ICampusesDatasource,
    ngSectionLabelsDatasource: ISectionLabelsDatasource,
    ngTermScheduleFlags: TermScheduleFlags
  ): ISectionsDatasource =>
    new Datasource<Section, ISectionsMeta>({
      builderType: Section,
      writeMode: 'clobber',
      updateMode: 'merge'
    })
      .includeMetasource<ISectionsMeta>((datasource) => {
        return new Metasource<Section, ISectionsMeta>(datasource)
          .includeAttribute('_campus', (section: Section) => {
            return _4.get(ngCampusesDatasource.read(), section.campus);
          })
          .includeAttribute('_section_times', (section: Section) => {
            return _4.filter(ngSectionTimesDatasource.metasource().derived(), (time) => time.section === section.id);
          })
          .includeAttribute('_fullLocationName', (section: Section) => {
            if (_4.isUndefined(section._campus)) {
              return '';
            }
            const specificLocation = _4.uniq(
              _4.compact(_4.map(section._section_times, (sectionTime) => `${_4.get(sectionTime, '_location.name')}`))
            );
            const campus = _4.get(section, '_campus.is_online')
              ? `Online Campus`
              : section._campus.name
              ? `${section._campus.name}`
              : 'TBD';
            return `${campus} ${specificLocation.length > 0 ? '|' : ''} ${specificLocation.join(' ')}`;
          })
          .includeAttribute('_section_labels', (section: Section) => {
            return _4.get(ngSectionLabelsDatasource.read(), section.id);
          })
          .includeAttribute('is_waitlisted', (section) => SectionStatusServant.isStatusWaitlisted(section))
          .includeAttributes((section) => {
            const disableRegistrationEligibilityForMultiSectionLinkedSections = (
              _section: Section,
              allScheduledSections: Section[]
            ) => {
              const isMultiSection = TermCourseServant.isMultiSectionCourse(_section._course);
              if (!isMultiSection) {
                return _section;
              }

              const allRequiredMultiSections = allScheduledSections.filter(
                (builtSection) =>
                  builtSection._course.id === _section.course && _4.get(builtSection, '_section_type.is_required')
              );

              const hasAnyNonEligibleSections = _4.some(allRequiredMultiSections, {
                _isEligibleForRegistration: false
              });

              if (!hasAnyNonEligibleSections) {
                return _section;
              }
              allRequiredMultiSections.forEach((requiredSection) => {
                requiredSection._isEligibleForRegistration = false;
              });
              return _section;
            };

            const sectionsWithStatus = datasource.values();

            const allSectionsWithMeta = datasource.metasource().composes(sectionsWithStatus);
            const allSectionTypes = ngTermSectionTypesDatasource.values();
            const linkedSections = ngLinkedSectionsModel.get(section.id);
            const _availability = ngSectionAvailabilityDatasource.get(section.id);
            if (_availability) {
              section._availability = _availability;
            }
            if (linkedSections) {
              section._linked_sections = linkedSections;
            }
            const waitListStatus = ngWaitlistStatusDatasource.get(section.id);
            if (waitListStatus) {
              section._section_status = waitListStatus;
            }

            const sectionType = ngTermSectionTypesDatasource.get(section.section_type);
            if (sectionType) {
              section._section_type = sectionType;
            }

            section._isEligibleForRegistration = SectionRegistrationServant.isEligibleForRegistration(
              section,
              allSectionsWithMeta,
              allSectionTypes,
              ngTermScheduleFlags
            );
            const sectionCourse = ngTermCoursesDatasource.get(section.course);
            if (sectionCourse) {
              section._course = ngTermCoursesDatasource.metasource().compose(sectionCourse);
            }
            disableRegistrationEligibilityForMultiSectionLinkedSections(section, allSectionsWithMeta);
            return section;
          });
      })
      .addHooks({
        $prePut: (section: Section) => {
          const availability = ngSectionAvailabilityDatasource.get(section.id);
          if (availability) {
            section._availability = availability;
          }
          const linkedSections = ngLinkedSectionsModel.get(section.id);
          if (linkedSections) {
            section._linked_sections = linkedSections;
          }
          section._section_status = ngWaitlistStatusDatasource.get(section.id)!;
          section._section_type = ngTermSectionTypesDatasource.get(section.section_type)!;
          section._course = _4.find(ngTermCoursesDatasource.metasource().derived(), { id: section.course });
          return section;
        }
      })
);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/sections/datasources/sections_datasource.ts